// Risk domain actions extracted from useAppState.js.
//
// Canonical-store contract (ADR-0001): risks are authoritative in
// tenant_risk_state, written per-record via risks-write. The optimistic
// in-memory mutation of state.risks is a projection only — a risk edit is
// durable ONLY once its canonical write is acknowledged. Every write is routed
// through riskWriteOutbox so a failure is visibly flagged per-record
// (`_writeStatus`), auto-retried with bounded backoff, and self-heals on
// reconnect — never silently lost by the blob save that strips these keys.
import { useCallback, useEffect, useRef, startTransition } from 'react';
import { generateId, now } from '../utils/formatting.js';
import { logActivity } from '../api/activityLogger.js';
import { emitEngineEvent } from '../engine/engineEventBus.js';
import {
  createRisk as apiCreateRisk,
  updateRisk as apiUpdateRisk,
  readRisk as apiReadRisk,
  stripTransientRiskFields,
} from '../api/riskApi.js';
import { keepableConflicts } from './riskConflicts.js';
import {
  enqueueRiskWrite,
  retryRiskWrite,
  discardRiskWrite,
  resetRiskOutbox,
} from './riskWriteOutbox.js';

function dispatchWriteError() {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('seasuite:write-error'));
    }
  } catch {
    /* non-browser — no-op */
  }
}

export function useRiskState({ updateState, tenantId, stateRef }) {
  // GH #7935 — what each edit is measured from. risks-write applies only what
  // an edit changed relative to its `base` to the risk as stored now, so the
  // base must be the stored risk the user's edit was made on — never the
  // optimistic projection, which may carry this user's own unsent edits.
  //
  // riskId -> the stored risk, held only while a write of that risk is queued
  // or in flight. Taken from the projection when the first edit is queued
  // (nothing un-stored is on screen then), advanced to what the server stored
  // as each write commits, and dropped once nothing is queued — the next edit
  // then starts from the projection, which the refresh loops keep current.
  const baseRef = useRef(new Map());

  // riskId -> { payload, base } of the edit last sent for that risk, and,
  // once the server refused it with a field clash, riskId -> that edit plus
  // the clashes. Keep mine (§13 d) resends exactly the refused edit, measured
  // from the same base, with the user's choice attached — so it can never
  // overwrite a field the user did not see clash.
  const lastSentRef = useRef(new Map());
  const clashRef = useRef(new Map());

  const sendUpdate = useCallback(
    (riskId, payload, base, keepMine, keepTheirs) => {
      lastSentRef.current.set(riskId, { payload, base });
      return apiUpdateRisk(tenantId, payload, base, { keepMine, keepTheirs });
    },
    [tenantId]
  );

  const readBase = useCallback(
    (riskId) => {
      const known = baseRef.current.get(riskId);
      if (known) return known;
      const shown = (stateRef.current?.risks || []).find((r) => r.id === riskId);
      return shown ? stripTransientRiskFields(JSON.parse(JSON.stringify(shown))) : null;
    },
    [stateRef]
  );

  // Patch a single risk record in the projection by id. patchFn receives the
  // current record and returns the replacement (or undefined to leave as-is).
  const patchRiskById = useCallback(
    (id, patchFn) => {
      updateState((s) => {
        const idx = (s.risks || []).findIndex((r) => r.id === id);
        if (idx < 0) return;
        const next = patchFn(s.risks[idx]);
        if (next !== undefined) s.risks[idx] = next;
      });
    },
    [updateState]
  );

  // Flag a record while its canonical write is pending/failed. Additive,
  // per-record, never persisted (stripped by the riskApi payload builders and
  // dropped by normaliseRiskState server-side).
  const markWriteStatus = useCallback(
    (id, status) => patchRiskById(id, (r) => ({ ...r, _writeStatus: status })),
    [patchRiskById]
  );

  // Adopt the server's returned canonical risk into the projection — the record
  // now reflects the committed row, not a guess, and `_writeStatus` clears
  // (the server record carries none). Patches by `localId`, the id the
  // optimistic row was inserted under — NOT necessarily serverRisk.id.
  // ID-GEN-001: on a create, the server always mints its own id (see
  // handlers/create.js), so localId is a throwaway client-side key that
  // will differ from serverRisk.id; patching by serverRisk.id would silently
  // no-op (patchRiskById finds nothing) and strand the optimistic row
  // forever under its local key.
  const adoptServerRisk = useCallback(
    (localId, serverRisk) => {
      if (!serverRisk || !serverRisk.id) return;
      patchRiskById(localId, () => serverRisk);
    },
    [patchRiskById]
  );

  // Route one record's canonical write through the outbox. onAck fires exactly
  // once, on acknowledgement, with the adopted server record — engine events and
  // activity logging hang off it so they reflect committed data (ADR §4.3).
  // Returns a promise resolving with the server-acknowledged record (callers
  // that need the committed record — e.g. a create that must learn its
  // server-minted id before writing a cross-reference — can await it; callers
  // that don't care are unaffected, same fire-and-forget behaviour as before).
  //
  // isCreate/onCreateConflict (ID-GEN-003): forwarded to the outbox so a 409
  // on a create is never blindly resent — see riskWriteOutbox.js's doc
  // comment and saveRisk's onCreateConflict below.
  //
  // An edit (not a create) is sent with its base (GH #7935): `base` is the
  // stored risk the edit was made on. The outbox sends one write per risk at a
  // time, and each commit advances the base, so a newer edit queued behind an
  // earlier one is measured from what the earlier one stored.
  const queueCanonicalWrite = useCallback(
    (id, writeFn, onAck, { isCreate, onCreateConflict, base } = {}) => {
      if (!isCreate && base && !baseRef.current.has(id)) baseRef.current.set(id, base);
      markWriteStatus(id, 'pending');
      return new Promise((resolve, reject) => {
        enqueueRiskWrite(id, writeFn, {
          isCreate,
          onCreateConflict,
          onCommitted: (serverRisk) => {
            if (!isCreate && serverRisk && serverRisk.id) {
              baseRef.current.set(id, stripTransientRiskFields(serverRisk));
            }
          },
          onSuccess: (serverRisk) => {
            baseRef.current.delete(id); // nothing queued for this risk now
            lastSentRef.current.delete(id);
            clashRef.current.delete(id);
            adoptServerRisk(id, serverRisk);
            onAck?.(serverRisk);
            resolve(serverRisk);
          },
          onStatus: (status, info) => {
            if (status === 'failed') {
              // A refused edit says why on the record itself: a field clash
              // names the field and both values (GH #7935), so the user can
              // see what to re-apply after reloading the stored version.
              const conflicts =
                info?.terminal &&
                Array.isArray(info.error?.conflicts) &&
                info.error.conflicts.length
                  ? info.error.conflicts
                  : undefined;
              if (info?.terminal) {
                baseRef.current.delete(id);
                const sent = lastSentRef.current.get(id);
                if (conflicts && sent) clashRef.current.set(id, { ...sent, conflicts });
                else clashRef.current.delete(id);
              }
              patchRiskById(id, (r) => ({
                ...r,
                _writeStatus: 'failed',
                _writeError: info?.terminal ? info.error?.message || null : null,
                _writeConflicts: conflicts,
              }));
              dispatchWriteError(); // coarse fallback alongside the per-record flag
              // Only reject on a terminal failure — a retryable one (409/5xx)
              // is still being auto-retried by the outbox and may yet succeed.
              if (info?.terminal) reject(info.error || new Error('risk write failed'));
            }
          },
        });
      });
    },
    [markWriteStatus, adoptServerRisk, patchRiskById]
  );

  // Clear the singleton outbox when the tenant changes / unmounts so a stale
  // queued write can never fire against a different tenant's freshly-loaded state.
  useEffect(
    () => () => {
      resetRiskOutbox();
      baseRef.current.clear();
      lastSentRef.current.clear();
      clashRef.current.clear();
    },
    [tenantId]
  );

  const saveRisk = useCallback(
    (risk, session) => {
      // Read current risks synchronously — the optimistic update below is
      // deferred, so we can't rely on its result to determine isEdit.
      const currentRisks = stateRef.current?.risks || [];
      const isEdit = currentRisks.some((r) => r.id === risk.id);
      // Read before the optimistic projection below replaces what is shown.
      const base = isEdit ? readBase(risk.id) : null;

      startTransition(() => {
        updateState((s) => {
          if (!s.risks) s.risks = [];
          if (!risk.actions) risk.actions = [];
          const idx = s.risks.findIndex((r) => r.id === risk.id);
          const projected = { ...risk, _writeStatus: 'pending' };
          if (idx >= 0) s.risks[idx] = projected;
          else s.risks = [projected, ...s.risks];
        });
      });

      // ID-GEN-003: on a create-conflict 409, don't blindly resend the same
      // payload (see riskWriteOutbox.js's doc comment). handlers/create.js
      // (ID-GEN-001) never trusts a client-supplied id, so simply re-issuing
      // this same create already gets a fresh, server-minted id — there is
      // no separate "strip the id" step needed, unlike a store that still
      // trusts a client id at create time. There is no reliable "did my own
      // earlier attempt already land?" check available here (no idempotency
      // key, and post-ID-GEN-001 there is no client-known target id to read
      // back), so this always re-issues rather than reconciling as already-
      // succeeded — a false re-issue only costs one extra create call, never
      // a duplicate or a wrong result.
      const onCreateConflict = () =>
        Promise.resolve({
          action: 'reissue',
          writeFn: () => apiCreateRisk(tenantId, risk),
        });

      // Returns the queueCanonicalWrite promise — most callers ignore it
      // (fire-and-forget, unchanged behaviour), but a caller that must learn
      // the server-minted id before writing a cross-reference (e.g. a
      // "propose risk from X" modal setting `linkedRiskId`) can await it.
      return queueCanonicalWrite(
        risk.id,
        () =>
          isEdit
            ? sendUpdate(risk.id, risk, baseRef.current.get(risk.id) || base)
            : apiCreateRisk(tenantId, risk),
        (serverRisk) => {
          emitEngineEvent('risk.saved', { risk: serverRisk || risk });
          if (session) {
            logActivity(tenantId, session, {
              module: 'Risks',
              actionType: isEdit ? 'edited' : 'created',
              // ID-GEN-001: on a create, risk.id is a throwaway local key —
              // the server mints the real id. Log the committed id.
              recordId: serverRisk?.id || risk.id,
              recordLabel: risk.name,
            });
          }
        },
        { isCreate: !isEdit, onCreateConflict: isEdit ? undefined : onCreateConflict, base }
      );
    },
    [updateState, tenantId, stateRef, queueCanonicalWrite, readBase, sendUpdate]
  );

  // #5305: the record used to be read back out of updateState's mutator
  // (`riskRef = { ...risk }` assigned inside it) — the same deferred-updater
  // shape #5292 fixed for findings (see updateFinding's comment in
  // FindingsPage.jsx). React only runs that mutator synchronously while the
  // fiber has no update already queued, so riskRef was readable on the next
  // line often enough to look correct and silently null the rest of the
  // time, skipping queueCanonicalWrite while the blob save still returned
  // 200 with `risks` stripped server-side. Built here, synchronously, from
  // stateRef.current instead — updateState is handed the finished object and
  // never asked for it back.
  const completeRiskAction = useCallback(
    (riskId, actionId, completionData = {}) => {
      const currentRisks = stateRef.current?.risks || [];
      const risk = currentRisks.find((r) => r.id === riskId);
      if (!risk || !risk.actions) return;
      const action = risk.actions.find((a) => a.id === actionId);
      // A cancelled action is settled with a reason, not work to complete (D-2 A).
      if (!action || action.status === 'cancelled') return;

      const updated = JSON.parse(JSON.stringify(risk));
      const updatedAction = updated.actions.find((a) => a.id === actionId);
      updatedAction.status = 'completed';
      updatedAction.completedAt = now();
      updatedAction.completedBy = completionData.userId || '';
      if (completionData.evidenceRef) {
        updatedAction.evidenceRefs = [
          ...(updatedAction.evidenceRefs || []),
          completionData.evidenceRef,
        ];
      }
      updated._writeStatus = 'pending';
      const base = readBase(riskId);

      updateState((s) => {
        const idx = (s.risks || []).findIndex((r) => r.id === riskId);
        if (idx < 0) return;
        s.risks[idx] = JSON.parse(JSON.stringify(updated));
      });
      queueCanonicalWrite(
        riskId,
        () => sendUpdate(riskId, updated, baseRef.current.get(riskId) || base),
        undefined,
        { base }
      );
    },
    [updateState, tenantId, stateRef, queueCanonicalWrite, readBase, sendUpdate]
  );

  const saveBulkRiskSnapshots = useCallback(
    (label, session) => {
      const snappedRisks = [];
      const bases = new Map((stateRef.current?.risks || []).map((r) => [r.id, readBase(r.id)]));
      updateState((s) => {
        const risks = s.risks || [];
        if (!risks.length) return;
        const ts = now();
        const lbl = label || null;
        risks.forEach((risk) => {
          if (!risk.scoreHistory) risk.scoreHistory = [];
          risk.scoreHistory.push({
            id: generateId(),
            timestamp: ts,
            label: lbl,
            impact: risk.impact,
            likelihood: risk.likelihood,
            postImpact: risk.selectedResolution?.postImpact ?? null,
            postLikelihood: risk.selectedResolution?.postLikelihood ?? null,
          });
          risk._writeStatus = 'pending';
          snappedRisks.push({ ...risk });
        });
      });

      // Persist each snapshotted record through its canonical endpoint. Before
      // ADR-0001 these mutations lived only in the blob, which state-write
      // strips for migrated tenants — so bulk snapshots were silently lost.
      for (const risk of snappedRisks) {
        const base = bases.get(risk.id);
        queueCanonicalWrite(
          risk.id,
          () => sendUpdate(risk.id, risk, baseRef.current.get(risk.id) || base),
          undefined,
          { base }
        );
      }

      if (session && snappedRisks.length > 0) {
        logActivity(tenantId, session, {
          module: 'Risks',
          actionType: 'snapshot',
          recordId: 'bulk',
          recordLabel: `Snapshot of ${snappedRisks.length} items` + (label ? ` — ${label}` : ''),
        });
      }
    },
    [updateState, tenantId, stateRef, queueCanonicalWrite, readBase, sendUpdate]
  );

  // #5305: same deferred-updater fix as completeRiskAction above — built
  // synchronously from stateRef.current rather than read back out of
  // updateState's mutator.
  const deleteRiskScoreSnapshot = useCallback(
    (riskId, snapshotId) => {
      const currentRisks = stateRef.current?.risks || [];
      const risk = currentRisks.find((r) => r.id === riskId);
      if (!risk || !risk.scoreHistory) return;

      const updated = JSON.parse(JSON.stringify(risk));
      updated.scoreHistory = updated.scoreHistory.filter((h) => h.id !== snapshotId);
      updated._writeStatus = 'pending';
      const base = readBase(riskId);

      updateState((s) => {
        const idx = (s.risks || []).findIndex((r) => r.id === riskId);
        if (idx < 0) return;
        s.risks[idx] = JSON.parse(JSON.stringify(updated));
      });
      // Same reason as saveBulkRiskSnapshots: this scoreHistory mutation must go
      // through the canonical endpoint or it is stripped from the blob and lost.
      queueCanonicalWrite(
        riskId,
        () => sendUpdate(riskId, updated, baseRef.current.get(riskId) || base),
        undefined,
        { base }
      );
    },
    [updateState, tenantId, stateRef, queueCanonicalWrite, readBase, sendUpdate]
  );

  // Per-record "Retry" affordance for a flagged (failed) canonical write —
  // re-arm the queued write; the flag clears when it acks.
  const retryRiskEdit = useCallback((riskId) => {
    retryRiskWrite(riskId);
  }, []);

  // Per-record "Discard" affordance — drop the queued write and replace the
  // optimistic edit with the server's canonical risk.
  //
  // The read can fail, and what happens then is the same question this whole
  // change is about (GH #6607). The queued write has already been dropped, so
  // if the read does not answer, the record on screen is the user's
  // UN-PERSISTED edit and nothing is going to store it. Clearing `_writeStatus`
  // there — which is what this did — removes the one marker telling them so,
  // and leaves an edit that exists nowhere looking like a saved record. That is
  // TRYGGR_INTENT.md §6.2 in miniature, reached by a discard rather than a save.
  //
  // So an unconfirmed discard keeps the record flagged, under its own status:
  // the write is gone (Retry cannot bring it back), but the screen still says
  // this is not stored, and reloading will show what the server actually holds.
  const discardRiskEdit = useCallback(
    async (riskId) => {
      discardRiskWrite(riskId);
      baseRef.current.delete(riskId);
      lastSentRef.current.delete(riskId);
      clashRef.current.delete(riskId);
      let fresh = null;
      try {
        fresh = await apiReadRisk(tenantId, riskId);
      } catch {
        /* handled below — an unconfirmed discard must not clear the marker */
      }
      if (fresh && fresh.id) {
        adoptServerRisk(riskId, fresh);
        return;
      }
      patchRiskById(riskId, (r) => {
        if (!r || r._writeStatus === undefined) return undefined;
        return { ...r, _writeStatus: 'discard-unconfirmed' };
      });
    },
    [tenantId, adoptServerRisk, patchRiskById]
  );

  // Per-record "Keep mine" (§13 d, GH #7935) for an edit refused because
  // someone else changed the same field: resend that edit, with the clashes
  // exactly as the server named them. The server honours each only while the
  // stored value is still the one the user was shown, so a third change in
  // between is refused again and named — never silently overwritten. Offered
  // only when every clash may be overwritten (keepableConflicts); otherwise
  // only "Use theirs" is, which keeps the other value at each clash.
  //
  // Both resend exactly the refused edit, measured from the same base, with
  // the user's choice for every clash they were shown (RiskWriteStatus lists
  // them all) — so neither can touch a field the user did not see clash.
  const resolveRiskClash = useCallback(
    (riskId, choice) => {
      const clash = clashRef.current.get(riskId);
      if (!clash) return null;
      const keep = keepableConflicts(clash.conflicts);
      if (choice === 'mine' && keep.length !== clash.conflicts.length) return null;
      clashRef.current.delete(riskId);
      patchRiskById(riskId, (r) =>
        r ? { ...r, _writeError: undefined, _writeConflicts: undefined } : undefined
      );
      return queueCanonicalWrite(
        riskId,
        () =>
          choice === 'mine'
            ? sendUpdate(riskId, clash.payload, clash.base, keep)
            : sendUpdate(riskId, clash.payload, clash.base, undefined, clash.conflicts),
        undefined,
        { base: clash.base }
      ).catch(() => null); // a refusal is shown on the record itself
    },
    [queueCanonicalWrite, sendUpdate, patchRiskById]
  );

  const keepMineRiskEdit = useCallback(
    (riskId) => resolveRiskClash(riskId, 'mine') || Promise.resolve(null),
    [resolveRiskClash]
  );

  // Per-record "Use theirs" (§13 d): the other value stays at every clashed
  // field and the user's other changes in the same edit still land. With no
  // refused edit to resend, it is the plain discard.
  const takeTheirsRiskEdit = useCallback(
    (riskId) => resolveRiskClash(riskId, 'theirs') || discardRiskEdit(riskId),
    [resolveRiskClash, discardRiskEdit]
  );

  return {
    saveRisk,
    completeRiskAction,
    saveBulkRiskSnapshots,
    deleteRiskScoreSnapshot,
    retryRiskEdit,
    discardRiskEdit,
    keepMineRiskEdit,
    takeTheirsRiskEdit,
  };
}
